import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Loading from '../home/Loading';
import AllQuestions from '../AllData/AllQuestions';
import './Style/BookMarkedQuestions.css';
import Latex from 'react-latex';
import TopNavbar from '../home/TopNavbar';
import TopHeader from '../home/TopHeader';

const BookmarkedQuestions = () => {
    const [bookmarkedQuestions, setBookmarkedQuestions] = useState([]);
    // New state for filtered questions
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // New state for selected subject filter
    const [selectedSubject, setSelectedSubject] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const questions = AllQuestions;
 
    // Load bookmarks from localStorage
    useEffect(() => {
        const fetchBookmarkedQuestions = () => {
            try {
                const storedBookmarks = localStorage.getItem('questionBookmarks');
                if (storedBookmarks) {
                    const bookmarksData = JSON.parse(storedBookmarks);
                    setBookmarkedQuestions(bookmarksData);
                }
            } catch (err) {
                setError('Failed to load bookmarks from storage');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchBookmarkedQuestions();
    }, []);

    // Effect to filter questions based on the selected subject
    useEffect(() => {
        if (selectedSubject === 'All') {
            setFilteredQuestions(bookmarkedQuestions);
        } else {
            setFilteredQuestions(bookmarkedQuestions.filter(bookmark => {
                const questionData = getQuestionData(bookmark.questionId.$oid);
                // Assuming questionData has a 'subject' property
                return questionData.subject === selectedSubject;
            }));
        }
        setCurrentPage(1); // Reset to page 1 on filter change
    }, [bookmarkedQuestions, selectedSubject]);

    // Get question data from questions array - handling both ID formats
    const getQuestionData = (questionId) => {
        // Handle both string ID and object ID format
        let idToFind;

        if (typeof questionId === 'object' && questionId.$oid) {
            // If ID is in format { $oid: "..." }
            idToFind = questionId.$oid;
        } else {
            // If ID is already a string
            idToFind = questionId;
        }

        return questions.find(q => {
            // Handle both question ID formats in the questions array too
            if (q._id && typeof q._id === 'object' && q._id.$oid) {
                return q._id.$oid === idToFind;
            } else {
                return q._id === idToFind;
            }
        }) || {};
    };

    // Get unique subjects from bookmarked questions
    const getSubjects = () => {
        const subjectsSet = new Set(['All']);
        bookmarkedQuestions.forEach(bookmark => {
            const questionData = getQuestionData(bookmark.questionId);
            if (questionData.subject) {
                subjectsSet.add(questionData.subject);
            }
        });
        return Array.from(subjectsSet);
    };

    // Remove specific bookmark from localStorage
    const handleRemoveBookmark = (questionId) => {
        try {
            const updatedBookmarks = bookmarkedQuestions.filter(
                bookmark => {
                    let bookmarkQuestionId;

                    if (typeof bookmark.questionId === 'object' && bookmark.questionId.$oid) {
                        bookmarkQuestionId = bookmark.questionId.$oid;
                    } else {
                        bookmarkQuestionId = bookmark.questionId;
                    }

                    let idToRemove;

                    if (typeof questionId === 'object' && questionId.$oid) {
                        idToRemove = questionId.$oid;
                    } else {
                        idToRemove = questionId;
                    }

                    return bookmarkQuestionId !== idToRemove;
                }
            );

            localStorage.setItem('questionBookmarks', JSON.stringify(updatedBookmarks));
            setBookmarkedQuestions(updatedBookmarks);
        } catch (err) {
            setError('Failed to remove bookmark');
            console.error(err);
        }
    };

    // Function to render text with LaTeX
    const renderWithLatex = (text) => {
        if (!text) return null;

        // Check if text contains LaTeX patterns
        if (text.includes('$') || text.includes('\\')) {
            return <Latex>{text}</Latex>;
        }
        return text;
    };

    if (loading) {
        return <Loading message='Loading bookmarked questions...' />
    }

    if (error) return <div className="error-message">{error}</div>;

    // Use filteredQuestions for pagination calculations
    const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
    const currentQuestions = filteredQuestions
        .slice()
        .reverse()
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="bookmarked-container">

            <TopNavbar name={"Bookmarked Questions " + "(" + filteredQuestions.length + ")"} />
            <TopHeader name={"Bookmarked Questions " + "(" + filteredQuestions.length + ")"} />

            {/* Subject Filter Buttons */}
            {filteredQuestions.length > 0 && (
                <div className="filter-controls">
                    {getSubjects().map(subject => (
                        <button
                            key={subject}
                            className={`filter-btn ${selectedSubject === subject ? 'active' : ''}`}
                            onClick={() => setSelectedSubject(subject)}
                        >
                            <p>{subject}</p>
                        </button>
                    ))}
                </div>
            )}

            {/* Conditional Rendering for Empty State */}
            {filteredQuestions.length === 0 ? (
                <div className="empty-state">
                    <p className="empty-message">
                        {selectedSubject === 'All'
                            ? "You haven't bookmarked any questions yet."
                            : `You haven't bookmarked any questions in ${selectedSubject}.`}
                    </p>
                    {selectedSubject === 'All' && (
                        <Link to="/practice-questions" className="browse-link">
                            Start Practicing Now
                        </Link>
                    )}
                </div>
            ) : (
                <div className="items-list">
                    {currentQuestions.map((bookmark, index) => {
                        const questionData = getQuestionData(bookmark.questionId);
                        const globalIndex = ((currentPage - 1) * itemsPerPage + index + 1);

                        return (
                            <div key={bookmark._id || bookmark.questionId} className="item-card">

                                <div className="item-content">
                                    {/* Display question name if available */}
                                    {questionData.name && (
                                        <h4 className="question-name">{questionData.name}</h4>

                                    )}

                                    {questionData.type === "dpp" || questionData.type === "yearrepeatedquestions" || (questionData.type === "previousyearquestions" && questionData.year === "2026") || (questionData.type === "previousyearquestions" && questionData.year === "2025") || (questionData.type === "previousyearquestions" && questionData.year === "2024") || (questionData.type === "previousyearquestions" && questionData.year === "2023") || (questionData.type === "previousyearquestions" && questionData.year === "2022") || (questionData.type === "previousyearquestions" && questionData.year === "2021") ? (<>

                                        {questionData.question ? (
                                            <p className="question-text">
                                                {renderWithLatex(questionData.question)}
                                            </p>
                                        ) : (
                                            <img
                                                src={`/questionsPhoto/${questionData.question_img}`}
                                                alt="Question"
                                            />
                                        )}
                                    </>) : (<>
                                        {questionData.question_img ? (
                                            <img
                                                src={`/questionsPhoto/${questionData.question_img}`}
                                                alt="Question"
                                                className="item-image"
                                            />
                                        ) : (
                                            <>{renderWithLatex(questionData.question)}</>
                                        )}
                                    </>)}

                                </div>
                                <div className="item-actions" style={{marginTop: "-30px"}}>
                                    <button
                                        onClick={() => handleRemoveBookmark(bookmark.questionId)}
                                        className="action-btn remove-btn"
                                    >
                                        Remove
                                    </button>
                                    <Link
                                        to={`/question-solution/${typeof bookmark.questionId === 'object' && bookmark.questionId.$oid
                                            ? bookmark.questionId.$oid
                                            : bookmark.questionId
                                            }`}
                                        className="action-btn view-btn"
                                    >
                                        View Solution
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination Controls - based on filteredQuestions */}
            {filteredQuestions.length > itemsPerPage && (
                <div className="pagination-controls">
                    <div className="pagination-info">
                        Total: {filteredQuestions.length} | Page {currentPage} of {totalPages}
                    </div>
                    <div className="pagination-buttons">
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookmarkedQuestions;